import { useEffect } from "react";
import {
  getMobileAppData,
  getRestaurantBackground,
  getRestaurantLogo,
} from "@/api/VenuesData/VenuesDataAPI";
import { useVenuesDataStore } from "@/store/useVenuesDataStore";
import {
  applyThemeColors,
  setThemeLoading,
  DEFAULT_THEME_COLORS,
} from "@/helpers/applyThemeColors";

/**
 * Fetch the restaurant's mobile-app colors and apply them. The app renders in
 * grayscale (see main.tsx) until this resolves.
 *
 * Lifted out of HomePage so it runs for every screen under a restaurant —
 * including /menu and the ordering flow, which are not HomePage children and
 * previously rendered unthemed on a cold first visit.
 */
export const useRestaurantTheme = (restaurantId: string) => {
  useEffect(() => {
    if (!restaurantId) return;

    getMobileAppData(restaurantId)
      .then((data) => {
        useVenuesDataStore.getState().setVenuesData({
          venue: data.venue,
          // Not just branding: the guest is charged this on top of any tip, so
          // every screen that shows a tip total needs it before Pay.
          tipFeeBps: data.tipFeeBps ?? 0,
          color: data.color,
        });
        applyThemeColors(data.color);
      })
      .catch(() => {
        // On failure, keep the last-known colors from previous screens/sessions
        // (persisted) rather than reverting to the white default theme.
        const persistedColor = useVenuesDataStore.getState().color;
        applyThemeColors(persistedColor ?? DEFAULT_THEME_COLORS);
      })
      .finally(() => {
        setThemeLoading(false);
      });
  }, [restaurantId]);

  // The venue's own artwork, published as custom properties that HomePage's
  // background and logo layers read. On failure the property stays unset and
  // the stylesheet falls back to the bundled image, so a missing or guarded
  // endpoint degrades to the old look rather than a blank screen.
  useEffect(() => {
    if (!restaurantId) return;

    let cancelled = false;
    const root = document.documentElement;

    /**
     * The stored URL is stable across uploads and the bucket serves it with
     * max-age=3600, so the version has to go in the query for a replaced photo
     * to appear at all. Same trick the RMS preview uses.
     */
    const publish = (property: string, image?: { imageUrl?: string; version?: string }) => {
      if (cancelled || !image?.imageUrl) return;
      const url = image.version
        ? `${image.imageUrl}?t=${image.version}&alt=media`
        : image.imageUrl;
      root.style.setProperty(property, `url("${url}")`);
    };

    getRestaurantBackground(restaurantId)
      .then((image) => publish("--venue-background-image", image))
      .catch(() => root.style.removeProperty("--venue-background-image"));

    getRestaurantLogo(restaurantId)
      .then((image) => {
        publish("--venue-logo-image", image);
        // An uploaded logo has its own aspect ratio, so fit it inside the
        // circle. The bundled SVG keeps its natural size.
        if (!cancelled && image?.imageUrl) {
          root.style.setProperty("--venue-logo-size", "contain");
        }
      })
      .catch(() => {
        root.style.removeProperty("--venue-logo-image");
        root.style.removeProperty("--venue-logo-size");
      });

    return () => {
      cancelled = true;
    };
  }, [restaurantId]);
};
